import type { Currency } from '@/bindings/Currency'
import type { QuantityStep } from '@/bindings/QuantityStep'
import { minorUnits } from '@/lib/currencies'

/**
 * Exact decimal arithmetic for order quantities and money, in integers: a
 * quantity is a count of its item's step, a price a count of the currency's
 * minor unit. Never a float sum (`0.1 + 0.2`); the wire form is the same
 * exact string the server validates (`QuantityStep::validate`).
 */

/** Mirrors `romi_domain::MAX_QUANTITY`. */
export const MAX_QUANTITY = 10_000

/** Steps per whole unit: `1` → 1, `0.5` → 2, `0.1` → 10. */
const SCALE: Record<QuantityStep, number> = { '1': 1, '0.5': 2, '0.1': 10 }
const DECIMALS: Record<QuantityStep, number> = { '1': 0, '0.5': 1, '0.1': 1 }

/** A decimal string as an integer count of `10^decimals`; `undefined` when it has more decimals. */
export function toScaled(value: string, decimals: number): number | undefined {
  const m = /^(\d+)(?:\.(\d*))?$/.exec(value.trim())
  if (!m) return undefined
  const frac = m[2] ?? ''
  if (frac.length > decimals) return undefined
  return Number(m[1]) * 10 ** decimals + Number(frac.padEnd(decimals, '0') || '0')
}

/** The inverse: a canonical decimal string with trailing zeros trimmed ("2", "1.5", "0.1"). */
export function fromScaled(scaled: number, decimals: number): string {
  const unit = 10 ** decimals
  const whole = Math.floor(scaled / unit)
  const frac = String(scaled % unit).padStart(decimals, '0').replace(/0+$/, '')
  return frac ? `${whole}.${frac}` : String(whole)
}

/** Steps in `quantity`; `undefined` when it is not on the step (a stale or foreign value). */
export function toSteps(quantity: string, step: QuantityStep): number | undefined {
  const scaled = toScaled(quantity, DECIMALS[step])
  if (scaled === undefined) return undefined
  const perStep = 10 ** DECIMALS[step] / SCALE[step]
  return scaled % perStep === 0 ? scaled / perStep : undefined
}

export function fromSteps(steps: number, step: QuantityStep): string {
  return fromScaled(steps * (10 ** DECIMALS[step] / SCALE[step]), DECIMALS[step])
}

/** `quantity ± step`, clamped to `[0, MAX_QUANTITY]`; an off-step input snaps to the nearest step first. */
export function addStep(quantity: string, step: QuantityStep, direction: 1 | -1): string {
  const current = toSteps(quantity, step) ?? (Math.round(Number(quantity) * SCALE[step]) || 0)
  const max = MAX_QUANTITY * SCALE[step]
  return fromSteps(Math.min(max, Math.max(0, current + direction)), step)
}

export function isMaxQuantity(quantity: string, step: QuantityStep): boolean {
  return (toSteps(quantity, step) ?? 0) >= MAX_QUANTITY * SCALE[step]
}

/** Typed text as a step count: on the step, within the cap, a comma decimal accepted; else `undefined`. */
export function parseSteps(input: string, step: QuantityStep): number | undefined {
  const steps = toSteps(input.replace(',', '.'), step)
  return steps === undefined || steps > MAX_QUANTITY * SCALE[step] ? undefined : steps
}

/** `parseSteps` as the canonical quantity string. */
export function parseQuantity(input: string, step: QuantityStep): string | undefined {
  const steps = parseSteps(input, step)
  return steps === undefined ? undefined : fromSteps(steps, step)
}

/**
 * The nearest quantity on the step, clamped to `MAX_QUANTITY`; `undefined` when
 * the text is not a plain decimal. Exactly three digits after the separator
 * ("1,000") is as likely a thousands group as a fraction, so it is refused too.
 */
export function snapQuantity(input: string, step: QuantityStep): string | undefined {
  const m = /^(\d+)(?:[.,](\d*))?$/.exec(input.trim())
  if (!m || m[2]?.length === 3) return undefined
  const value = Number(`${m[1]}.${m[2] ?? ''}`)
  return fromSteps(Math.min(MAX_QUANTITY * SCALE[step], Math.round(value * SCALE[step])), step)
}

/**
 * `unit_price × quantity` in minor units, midpoint away from zero — the
 * server's `Currency::round` (T17). A preview only; the placed order's stored
 * totals are the truth.
 */
export function lineTotalMinor(unitPrice: string, quantity: string, step: QuantityStep, currency: Currency): number {
  const decimals = minorUnits(currency)
  const price = toScaled(unitPrice, decimals) ?? Math.round(Number(unitPrice) * 10 ** decimals)
  const steps = toSteps(quantity, step) ?? 0
  const scale = SCALE[step]
  // Integer division with a half-up remainder; all operands are exact integers.
  const raw = price * steps
  const q = Math.floor(raw / scale)
  return raw - q * scale >= scale / 2 ? q + 1 : q
}

/** Minor units back to the currency's decimal string ("45000", "12.50"). */
export function fromMinor(minor: number, currency: Currency): string {
  const decimals = minorUnits(currency)
  const unit = 10 ** decimals
  return decimals === 0 ? String(minor) : `${Math.floor(minor / unit)}.${String(minor % unit).padStart(decimals, '0')}`
}
